/**
 * FooterConfigBlock View component.
 * Renders a readable summary of the footer configuration for editors.
 * The actual footer is rendered by Footer.jsx.
 */

import React from 'react';

const SOCIAL_LABELS = {
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  bluesky: 'Bluesky',
  youtube: 'YouTube',
  mastodon: 'Mastodon',
  twitter: 'X (Twitter)',
  instagram: 'Instagram',
  github: 'GitHub',
};

const resolveHrefDisplay = (href) => {
  if (!href) return '—';
  if (typeof href === 'object') return href['@id'] || JSON.stringify(href);
  return href;
};

/**
 * Summary of a single column item — link or text block.
 */
const ColumnItemSummary = ({ item }) => {
  const type = item.type || 'link';
  if (type === 'text') {
    return (
      <li className="footer-config-view__item footer-config-view__item--text">
        <em>Text block: </em>
        <span
          dangerouslySetInnerHTML={{
            __html: item.body?.data || item.body || '(empty)',
          }}
        />
      </li>
    );
  }
  return (
    <li className="footer-config-view__item footer-config-view__item--link">
      <span>{item.label || '(no label)'}</span>
      {' — '}
      <code>{resolveHrefDisplay(item.href)}</code>
    </li>
  );
};

const FooterConfigView = ({ data }) => {
  console.group('[FooterConfigBlock] View data');
  console.log('Intro:', {
    showIntro: data.showIntro,
    introLogoType: data.introLogoType,
    introCustomLogo: data.introCustomLogo,
    introText: data.introText,
  });
  console.log('Columns:', data.columns);
  console.log('Social:', data.social);
  console.log('Copyright:', data.copyright);
  console.log('Attribution:', data.attribution);
  console.log('Footer bar links:', data.footerBarLinks);
  console.log('Show back to top:', data.showBackToTop);
  console.groupEnd();

  const hasColumns = data.columns?.length > 0;
  const hasSocial = data.social?.length > 0;
  const hasFooterBar = data.footerBarLinks?.length > 0;

  return (
    <div className="footer-config-view">
      <div className="footer-config-view__header">
        <i className="bi bi-layout-text-sidebar" />
        <h2>Footer Configuration</h2>
        <p>
          This page configures the site footer. The content below is a summary
          for editors — visitors see the rendered footer at the bottom of every
          page.
        </p>
      </div>

      {/* Intro column */}
      {data.showIntro && (
        <div className="footer-config-view__section">
          <h3>Intro Column</h3>
          <dl>
            <dt>Logo</dt>
            <dd>
              {data.introLogoType === 'site' && 'Site logo (from Site Setup)'}
              {data.introLogoType === 'custom' && (
                <>
                  Custom: <code>{resolveHrefDisplay(data.introCustomLogo)}</code>
                </>
              )}
              {data.introLogoType === 'none' && 'No logo'}
              {!data.introLogoType && 'Site logo (default)'}
            </dd>
            {data.introText && (
              <>
                <dt>Intro text</dt>
                <dd
                  dangerouslySetInnerHTML={{
                    __html: data.introText?.data || data.introText,
                  }}
                />
              </>
            )}
          </dl>
        </div>
      )}

      {/* Link columns */}
      <div className="footer-config-view__section">
        <h3>Link Columns ({data.columns?.length || 0})</h3>
        {hasColumns ? (
          <div className="footer-config-view__columns">
            {data.columns.map((col, i) => (
              <div key={i} className="footer-config-view__column">
                <strong>{col.heading || <em>No heading</em>}</strong>
                {col.links?.length > 0 ? (
                  <ul>
                    {col.links.map((item, j) => (
                      <ColumnItemSummary key={j} item={item} />
                    ))}
                  </ul>
                ) : (
                  <p className="footer-config-view__empty">
                    No items added yet.
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="footer-config-view__empty">
            No columns configured. Open the block and add columns in the
            sidebar.
          </p>
        )}
      </div>

      {/* Copyright & Social */}
      <div className="footer-config-view__section">
        <h3>Copyright & Social</h3>
        <dl>
          <dt>Copyright text</dt>
          <dd>{data.copyright || <em>Not set</em>}</dd>
          <dt>Back to top</dt>
          <dd>{data.showBackToTop !== false ? 'Shown' : 'Hidden'}</dd>
          {data.attribution && (
            <>
              <dt>Attribution</dt>
              <dd
                dangerouslySetInnerHTML={{
                  __html: data.attribution?.data || data.attribution,
                }}
              />
            </>
          )}
        </dl>
        {hasSocial ? (
          <ul className="footer-config-view__social">
            {data.social.map((item, i) => (
              <li key={i}>
                <strong>
                  {SOCIAL_LABELS[item.platform] || item.platform}
                </strong>
                {' — '}
                <code>{resolveHrefDisplay(item.href)}</code>
              </li>
            ))}
          </ul>
        ) : (
          <p className="footer-config-view__empty">
            No social links configured.
          </p>
        )}
      </div>

      {/* Footer bar */}
      <div className="footer-config-view__section">
        <h3>Footer Bar Links ({data.footerBarLinks?.length || 0})</h3>
        {hasFooterBar ? (
          <ul>
            {data.footerBarLinks.map((link, i) => (
              <li key={i}>
                <span>{link.label}</span>
                {' — '}
                <code>{resolveHrefDisplay(link.href)}</code>
              </li>
            ))}
          </ul>
        ) : (
          <p className="footer-config-view__empty">
            No footer bar links configured.
          </p>
        )}
      </div>
    </div>
  );
};

export default FooterConfigView;
